/**
 * Collapsible Component
 *
 * Simple collapsible wrapper with smooth animations
 */

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

interface CollapsibleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  className?: string;
}

interface CollapsibleTriggerProps {
  children: React.ReactElement;
  asChild?: boolean;
  className?: string;
}

interface CollapsibleContentProps {
  children: React.ReactNode;
  className?: string;
}

const CollapsibleContext = React.createContext<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
}>({
  open: false,
  onOpenChange: () => {},
});

export const Collapsible: React.FC<CollapsibleProps> = ({
  open,
  onOpenChange,
  children,
  className,
}) => {
  return (
    <CollapsibleContext.Provider value={{ open, onOpenChange }}>
      <div className={cn('', className)}>{children}</div>
    </CollapsibleContext.Provider>
  );
};

export const CollapsibleTrigger: React.FC<CollapsibleTriggerProps> = ({
  children,
  asChild = false,
  className,
}) => {
  const { onOpenChange, open } = React.useContext(CollapsibleContext);

  const handleClick = () => {
    onOpenChange(!open);
  };

  if (asChild && React.isValidElement(children)) {
    const el = children as React.ReactElement<any>;
    return React.cloneElement(
      el as any,
      {
        onClick: handleClick,
        className: cn((el.props as any).className, className),
      } as any
    );
  }

  return (
    <button
      type='button'
      onClick={handleClick}
      className={cn('cursor-pointer', className)}
    >
      {children}
    </button>
  );
};

export const CollapsibleContent: React.FC<CollapsibleContentProps> = ({
  children,
  className,
}) => {
  const { open } = React.useContext(CollapsibleContext);
  const [height, setHeight] = useState<number | undefined>(
    open ? undefined : 0
  );
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ref.current) return;

    if (open) {
      const scrollHeight = ref.current.scrollHeight;
      setHeight(scrollHeight);

      // After animation completes, remove the height constraint
      const timer = setTimeout(() => {
        setHeight(undefined);
      }, 300);

      return () => clearTimeout(timer);
    } else {
      // First set the height to current scroll height, then to 0
      const scrollHeight = ref.current.scrollHeight;
      setHeight(scrollHeight);

      // Use requestAnimationFrame to ensure the height is set before collapsing
      requestAnimationFrame(() => {
        setHeight(0);
      });
    }
  }, [open]);

  return (
    <div
      ref={ref}
      className={cn(
        'overflow-hidden transition-all duration-300 ease-in-out',
        className
      )}
      style={{
        height: height,
      }}
    >
      <div className='p-0'>{children}</div>
    </div>
  );
};

// Re-export for convenience
export { Collapsible as CollapsibleRoot };
export default Collapsible;
